/**
 * PaSubscriptionCards — renders the subscription block's product cards from
 * the basket and the customer's active products.
 *
 * Data contract (native, no guessing):
 * - Basket lines are written only by the basket API in frontend.js and always
 *   carry the numeric productConfID in `id` (see addToBasket).
 * - Cards carry that same id in the buy button's data-productid attribute
 *   (src/subscription_block/block.js).
 * - The customer's active products and the block's available products are
 *   pushed in by subscription.js when its API responses land
 *   (setCurrent / setAvailable).
 *
 * Card states (priority order):
 *   1. current — customer owns the product: "Dit abonnement" + ✓, locked
 *   2. added   — the product is in the basket: "Tilføjet til kurv" + ✓
 *   3. base    — default label, or "Opgrader til …"/"Nedgrader til …" when a
 *                current product level is known
 *
 * The subscription_select block manages its own cards (setSelectionUnique in
 * frontend.js) and is intentionally NOT handled here.
 */

import { readBasket, cartLabels, onBasketUpdate } from './cart_manager.js';
import { formatPrice } from './format.js';

const CARD_SEL = '.subscription-product-panel';
const BUTTON_SEL = '.subscription-card-product-buy-btn button, .subscription-card-product-buy-btn a';

// Pushed in by subscription.js; see setCurrent() / setAvailable().
let currentIds = new Set();
let currentLevel = null;
let availableById = {};

/* ------------------------------------------------------------------ *
 * Card discovery
 * ------------------------------------------------------------------ */

function cardEntries() {
    const entries = [];
    document.querySelectorAll( CARD_SEL ).forEach( card => {
        const button = card.querySelector( BUTTON_SEL );
        const pid = button && button.getAttribute( 'data-productid' );
        if ( pid ) {
            entries.push( { card, button, pid: String( pid ).trim() } );
        }
    } );
    return entries;
}

function basketIdSet() {
    //Selections only — a 'remove' line is a cancellation of an owned product,
    //not a selection, and must not label a card "Tilføjet til kurv" (shared
    //semantics: cart_manager.getCartState). Null-guarded: a malformed line
    //must not crash the whole sync.
    return new Set( readBasket()
        .filter( line => line && line.action !== 'remove' )
        .map( line => String( line.id ) ) );
}

/* ------------------------------------------------------------------ *
 * State resolution
 * ------------------------------------------------------------------ */

function isCurrent( pid ) {
    return currentIds.has( pid );
}

/**
 * Product levels from the subscription block's data-products attribute,
 * parsed once per sync.
 *
 * @return {Object} Map of productId → level.
 */
function blockLevels() {
    const map = {};
    document.querySelectorAll( '.subscription-container[data-products]' ).forEach( container => {
        let products;
        try {
            products = JSON.parse( container.getAttribute( 'data-products' ) );
        } catch ( e ) {
            return;
        }
        ( Array.isArray( products ) ? products : [] ).forEach( product => {
            if ( product && product.productId != null ) {
                map[ String( product.productId ) ] = parseFloat( product.level );
            }
        } );
    } );
    return map;
}

/**
 * Active monthly price for a card's product, from the available-products
 * response pushed in by subscription.js.
 *
 * @param {string} pid The card's product id.
 * @return {?number} Monthly price, or null when the product is unknown.
 */
function monthlyPrice( pid ) {
    const pconf = availableById[ pid ];
    if ( ! pconf || typeof window.getSubscriptionTotal !== 'function' ) {
        return null;
    }
    return window.getSubscriptionTotal( pconf );
}

/* ------------------------------------------------------------------ *
 * Rendering
 * ------------------------------------------------------------------ */

/**
 * Wrap the button content in .pa-btn-text / .pa-btn-icon spans so the label
 * and the ✓ icon can be updated independently.
 *
 * @param {Element} button The CTA button element.
 * @return {{text: Element, icon: Element}} The label and icon spans.
 */
function ensureWrap( button ) {
    let text = button.querySelector( '.pa-btn-text' );
    let icon = button.querySelector( '.pa-btn-icon' );
    if ( text && icon ) {
        return { text, icon };
    }
    const currentText = String( button.textContent || '' ).replace( /\s+/g, ' ' ).trim();
    button.innerHTML = '';
    text = document.createElement( 'span' );
    text.className = 'pa-btn-text';
    text.textContent = currentText;
    icon = document.createElement( 'span' );
    icon.className = 'pa-btn-icon';
    icon.setAttribute( 'aria-hidden', 'true' );
    button.appendChild( text );
    button.appendChild( icon );
    return { text, icon };
}

function setButton( button, rawLabel, icon ) {
    const label = rawLabel == null ? '' : String( rawLabel );
    const wrap = ensureWrap( button );
    if ( wrap.text.textContent !== label ) {
        wrap.text.textContent = label;
    }
    const iconText = icon || '';
    if ( wrap.icon.textContent !== iconText ) {
        wrap.icon.textContent = iconText;
    }
    if ( button.getAttribute( 'aria-label' ) !== label ) {
        button.setAttribute( 'aria-label', label );
    }
}

// The block's own button text (e.g. "Køb") is the base label; capture it
// before the first rewrite so it can be restored on deselect.
function captureDefault( entry ) {
    if ( entry.card.getAttribute( 'data-pa-default' ) ) {
        return;
    }
    const wrap = entry.button.querySelector( '.pa-btn-text' );
    const text = String( ( wrap || entry.button ).textContent || '' ).replace( /\s+/g, ' ' ).trim();
    if ( text ) {
        entry.card.setAttribute( 'data-pa-default', text );
    }
}

function baseLabel( entry, conf, levels ) {
    // Upgrade/downgrade prefix when a current product level is known.
    const ownLevel = levels[ entry.pid ];
    if ( ownLevel != null && isFinite( ownLevel ) && currentLevel != null && ownLevel !== currentLevel ) {
        const verb = ownLevel > currentLevel ? conf.upgrade : conf.downgrade;
        const price = formatPrice( monthlyPrice( entry.pid ) );
        if ( price ) {
            return verb + ' ' + price + ' kr./md.';
        }
        const nameEl = entry.card.querySelector( '.subscription-card-product-name' );
        const name = nameEl ? String( nameEl.textContent || '' ).trim() : '';
        if ( name ) {
            return verb + ' ' + name;
        }
    }

    return entry.card.getAttribute( 'data-pa-default' ) || conf.base;
}

function setLocked( button, locked ) {
    if ( button.tagName === 'BUTTON' ) {
        button.disabled = locked;
    } else if ( locked ) {
        button.setAttribute( 'aria-disabled', 'true' );
    } else {
        button.removeAttribute( 'aria-disabled' );
    }
}

function applyState( entry, basketIds, conf, levels ) {
    const { card, button, pid } = entry;

    captureDefault( entry );

    if ( isCurrent( pid ) ) {
        card.classList.add( 'pa-is-current' );
        card.classList.remove( 'in-basket' );
        button.classList.add( 'pa-is-current' );
        button.classList.remove( 'is-added', 'in-basket' );
        setButton( button, conf.current, '✓' );
        setLocked( button, true );
        return;
    }

    card.classList.remove( 'pa-is-current' );
    button.classList.remove( 'pa-is-current' );
    setLocked( button, false );

    if ( basketIds.has( pid ) ) {
        card.classList.add( 'in-basket' );
        button.classList.add( 'is-added', 'in-basket' );
        setButton( button, conf.added, '✓' );
        return;
    }

    card.classList.remove( 'in-basket' );
    button.classList.remove( 'is-added', 'in-basket' );
    setButton( button, baseLabel( entry, conf, levels ), '' );
}

export function sync() {
    const entries = cardEntries();
    if ( ! entries.length ) {
        return;
    }
    const basketIds = basketIdSet();
    const conf = cartLabels();
    const levels = blockLevels();
    entries.forEach( entry => applyState( entry, basketIds, conf, levels ) );
}

/* ------------------------------------------------------------------ *
 * Data pushed in by subscription.js
 * ------------------------------------------------------------------ */

/**
 * Set the customer's active products (from /customer/products). Every active
 * product's card renders locked as "Dit abonnement".
 *
 * @param {Array}          productIds Ids of the customer's active products.
 * @param {string|number=} level      The current product's level, if known.
 */
export function setCurrent( productIds, level ) {
    currentIds = new Set( ( productIds || [] ).map( id => String( id ) ) );
    const parsed = parseFloat( level );
    currentLevel = isFinite( parsed ) ? parsed : null;
    sync();
}

/**
 * Set the block's available products (from /customer/available-products),
 * used for the upgrade/downgrade price labels.
 *
 * @param {Array} products The available-products response.
 */
export function setAvailable( products ) {
    availableById = {};
    ( Array.isArray( products ) ? products : [] ).forEach( product => {
        if ( product && product.id != null ) {
            availableById[ String( product.id ) ] = product;
        }
    } );
    sync();
}

/* ------------------------------------------------------------------ *
 * Mutations — native basket API only.
 * ------------------------------------------------------------------ */

/**
 * Drop other in-basket products from the subscription cards, keeping
 * productId (single-select), then re-render.
 *
 * @param {string|number} productId The product to keep in the basket.
 */
export function singleSelect( productId ) {
    const pid = String( productId );
    const basketIds = basketIdSet();
    cardEntries().forEach( entry => {
        if ( entry.pid !== pid && ! isCurrent( entry.pid ) && basketIds.has( entry.pid ) ) {
            window.removeFromBasket( entry.pid );
        }
    } );
    sync();
}

/**
 * Select a product (called by subscription.js): add it via the native basket
 * API when absent and enforce single-select. A product already in the basket
 * stays there — repeat clicks are no-ops, and a failed add (unmet product
 * requirements) leaves the rest of the basket untouched.
 *
 * @param {string|number} productId The product to select.
 * @param {string}        provModel The product's provisioning model.
 * @param {string}        action    Basket action: add/upgrade/downgrade/update.
 * @return {boolean} True when the product is in the basket afterwards.
 */
export function select( productId, provModel, action ) {
    const pid = String( productId );
    if ( isCurrent( pid ) ) {
        return false;
    }
    if ( ! window.findInBasket( productId ) ) {
        if ( window.addToBasket( productId, provModel, action || 'add', 1, Date.now() ) === false ) {
            sync();
            return false;
        }
    }
    singleSelect( pid );
    return true;
}

/* ------------------------------------------------------------------ *
 * Event wiring
 * ------------------------------------------------------------------ */

let initialized = false;

export function init() {
    if ( initialized || ! document.querySelector( CARD_SEL ) ) {
        return;
    }
    initialized = true;

    //Coalesces the pa_basket_update burst from singleSelect into one render.
    onBasketUpdate( sync );
    sync();
}

const api = { init, sync, setCurrent, setAvailable, select, singleSelect };

export default api;
